import { useEffect, useState } from "react"
import {
	IconPhotoPlusOutline18 as ImagePlusIcon,
	IconArrowRotateAnticlockwiseOutline18 as RestoreIcon,
	IconTrashOutline18 as TrashIcon,
} from "nucleo-ui-outline-18"

import { Button } from "@productlane/ui"

// A draft created a moment ago may not have reached the server yet, so its
// cover 404s; a few spaced retries cover that before the plain upload box.
export const GENERATED_COVER_RETRY_DELAYS_MS = [800, 2000, 5000]

/** Retries a generated cover on error; `failed` once every retry is spent. */
export function useRetryingCoverSrc(src: string) {
	const [attempt, setAttempt] = useState(0)
	const [failed, setFailed] = useState(false)
	const [retryPending, setRetryPending] = useState(false)

	useEffect(() => {
		setAttempt(0)
		setFailed(false)
		setRetryPending(false)
	}, [src])

	useEffect(() => {
		if (!retryPending) return
		const timer = setTimeout(() => {
			setRetryPending(false)
			setAttempt((a) => a + 1)
		}, GENERATED_COVER_RETRY_DELAYS_MS[attempt])
		return () => clearTimeout(timer)
	}, [retryPending, attempt])

	const separator = src.includes("?") ? "&" : "?"
	return {
		/** null while a retry waits or after the last one failed. */
		imgSrc:
			retryPending || failed
				? null
				: attempt
					? `${src}${separator}r=${attempt}`
					: src,
		failed,
		onError: () => {
			if (attempt < GENERATED_COVER_RETRY_DELAYS_MS.length)
				setRetryPending(true)
			else setFailed(true)
		},
	}
}

const coverHint = (
	<>
		<ImagePlusIcon className="h-5 w-5 flex-none" />
		<p className="pt-2 font-medium">Cover image</p>
		<span className="pt-1 text-xs font-normal">
			Ideal dimensions are 1200 x 627
		</span>
	</>
)

/** The plain upload box: no generated cover, or the author removed it. */
export function CoverUploadBox({
	onSelect,
	onRestore,
}: {
	onSelect: () => void
	/** Brings back a generated cover the author removed. */
	onRestore?: () => void
}) {
	return (
		<div className="relative">
			<button
				onClick={onSelect}
				type="button"
				data-cover-upload-box=""
				className="block w-full"
			>
				<div className="flex h-[20.625rem] flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-secondary hover:opacity-75 dark:border-gray-700/80 dark:bg-gray-800 [&_span]:text-tertiary [&_svg]:text-gray-400">
					{coverHint}
				</div>
			</button>
			{onRestore && (
				<Button
					variant="outline"
					Icon={RestoreIcon}
					onClick={onRestore}
					className="absolute right-4 top-4"
					data-restore-generated-cover=""
				>
					Generate cover
				</Button>
			)}
		</div>
	)
}

export function GeneratedCoverButton({
	src,
	onSelect,
	onRemove,
}: {
	src: string
	onSelect: () => void
	/** Omitted for viewers, who can't change the cover. */
	onRemove?: () => void
}) {
	const { imgSrc, failed, onError } = useRetryingCoverSrc(src)

	if (failed) return <CoverUploadBox onSelect={onSelect} />

	return (
		<div className="group relative">
			<button
				onClick={onSelect}
				type="button"
				className="relative block aspect-[1200/630] w-full overflow-hidden rounded-lg bg-gray-100 ring-1 ring-black/10 dark:bg-gray-800 dark:ring-white/10"
			>
				{imgSrc && (
					<img
						src={imgSrc}
						alt=""
						className="block size-full object-cover"
						onError={onError}
					/>
				)}
				<div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 [&_span]:text-white/80">
					{coverHint}
				</div>
			</button>
			{onRemove && (
				<button
					className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900/20 opacity-0 shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-lg hover:bg-gray-900/30 hover:ring-white/30 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100 dark:bg-black/20 dark:hover:bg-black/30"
					type="button"
					onClick={onRemove}
					data-remove-generated-cover=""
					title="Delete cover image"
					aria-label="Delete cover image"
				>
					<TrashIcon className="h-4 w-4 flex-none transform-gpu text-white drop-shadow-sm" />
				</button>
			)}
		</div>
	)
}
